import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { bmxurl, fillselectableresourcestrings, selectableIdsToSave, withSelectableIds } from 'src/globals';
import { MoveBookingService } from './move-booking.service';

//The booking we have read for a change, rebuilt the way Edit Booking builds it when it opens a
//booking - everything a change of admissions needs, without Edit Booking's screen.
export interface bookingforedit {
  booking: any;              //The booking, prepared as Edit Booking prepares NewBooking.
  bookingResources: any[];   //The booking's own resources, as saved.
  selectedPackage: any;      //The package's row from the package list.
  admissions: any[];         //The package's admissions, carrying the booking's numbers.
  currentUpgrade: any;       //The package upgrade the booking has, or null.
}

//Changing a booking's admissions - the one copy of how it is done, shared by Edit Booking and
//the AI staff assistant so the two can never change a booking differently. Each piece is lifted
//from Edit Booking unchanged, and Edit Booking now calls these instead of its own copies:
//
//  1. Linked admissions are worked out (an admission tied to others takes its number, or its
//     maximum, from them).
//  2. The resources the booking is using are worked out from the package's, by which rooms or
//     tables it has (the live part of donePartyRooms), plus any upgrade's resources.
//  3. /Availability is asked for the booking's day with the new numbers, and the slot at the
//     booking's current time is found - no slot, no room for that many.
//  4. The booking is rebuilt from that slot (bookSlot) and saved with PUT savebooking/true,
//     refused if it would be left without admissions.
//
//bookSlot's rebuild is also what Edit Booking uses when a package or a time is changed there.
@Injectable({
  providedIn: 'root'
})
export class BookingEditService {

  constructor(private http: HttpClient, private moveBookingService: MoveBookingService) { }

  //#region The shared pieces

  //Admissions linked to others: either their number is the total of the ones they are linked to
  //(and fixed), or only their maximum is (onlysetmax). Changes the admissions given.
  applyLinkedAdmissions(admissions: any[]): void {
    (admissions ?? []).forEach(adm => {
      if (adm.linkedadmissions && adm.linkedadmissions.length > 0) {
        let totalNumberOfLinked = 0;

        adm.linkedadmissions.forEach(ladm => {
          const matching = admissions.find(innerAdm => innerAdm.admissionid === ladm.admissionid);
          if (matching) {
            totalNumberOfLinked += Number(matching.noofpeople);
          }
        });

        if (adm.onlysetmax) {
          adm.max = Number(totalNumberOfLinked);

          if (adm.max < adm.min) {
            adm.max = adm.min;
          }

          if (Number(adm.noofpeople) > adm.max) {
            adm.noofpeople = adm.max;
          }

          adm.fixed = false;
        } else {
          adm.noofpeople = Number(totalNumberOfLinked);
          adm.fixed = true;
        }
      }
    });
  }

  //Marks, on the package's resources, the rooms and tables the booking has - the ignore flag is
  //borrowed for it. chosenResources() then reads the marks.
  markBookedResources(possibleResources: any[], bookingResources: any[]): void {
    (possibleResources ?? []).forEach(PR => {
      if (PR.mode == 'COMPLEX')
      {
        (PR.resources ?? []).forEach((innerPR: any) => {
          let index = 0;
          bookingResources.forEach(BR => {
            if(index == PR.orderindex)
            {
              if(innerPR.id == BR.id)
              {
                innerPR.ignore = true; //temporarily using the ignore flag to mark these resources.
              }
            }
            index++;
          });
        });

        if(PR.selectableresourcestrings != null && PR.selectableresourcestrings.length > 0)
        {
          PR.selectableresourcestrings.forEach((innerPR: any) => {
            let index = 0;
            bookingResources.forEach(BR => {
              if(index == PR.orderindex)
              {
                if(innerPR.id == BR.id)
                {
                  innerPR.ignore = true; //temporarily using the ignore flag to mark these resources.
                }
              }
              index++;
            });
          });

          if(PR.selectionmode === 'SYSTEMANY' || PR.selectionmode === 'SYSTEMLIST' || PR.selectionmode === 'ANY')
          {
            PR.selectableresourcestrings.forEach((innerPR: any) => {
              const matched = bookingResources.some((BR: any) => {
                // Case A: container has exactly 1 resource — a specific choice was previously saved
                if (BR.id === PR.id && BR.resources?.length === 1 && BR.resources[0].id === innerPR.id) return true;
                // Case B: resources were exploded — each BR is an inner resource with originatingid pointing to PR
                if (BR.originatingid === PR.id && BR.id === innerPR.id) return true;
                // Case C: resource group — child tables carry wascontainer pointing to the group's resourceId
                if (BR.wascontainer && BR.wascontainer === innerPR.resourceId) return true;
                return false;
              });
              if(matched) { innerPR.ignore = true; }
            });
          }
        }
      }
    });
  }

  //The resources the booking is using, worked out from the package's resources and the marks
  //markBookedResources() (or staff, on Edit Booking) put on them.
  chosenResources(possibleResources: any[], allowResourcesChangedManually: boolean): any[] {
    const chosen: any[] = [];

    [...(possibleResources ?? [])].forEach(PR => {
      if(PR.selectionmode == 'LIST' || PR.selectionmode == 'SYSTEMLIST')
      {
        (PR.selectableresourcestrings ?? []).forEach(innerPR => {
          if(innerPR.ignore)
          {
            chosen.push(this.chosenResource(PR, innerPR));
          }
        });
      }
      else if (PR.selectionmode == 'ANY' || PR.selectionmode == 'SYSTEMANY')
      {
        if (PR.selectableresourcestrings?.some((r: any) => r.ignore) && (PR.selectionmode === 'ANY' || allowResourcesChangedManually))
        {
          PR.selectableresourcestrings.forEach((innerPR: any) => {
            if(innerPR.ignore)
            {
              chosen.push(this.chosenResource(PR, innerPR));
            }
          });
        }
        else
        {
          chosen.push(PR);
        }
      }
      else
      {
        chosen.push(PR);
      }
    });

    //Sent to the API, which holds just the ids - see selectableResourceIds.
    return chosen.map(res => ({ ...res, selectableresourcestrings: selectableIdsToSave(res) }));
  }

  //A package resource group holding just the one resource chosen inside it.
  private chosenResource(PR: any, innerPR: any): any {
    return {
      groupname: PR.groupname,
      id: PR.id,
      resourceId: PR.resourceId,
      name: PR.name,
      useClasses: PR.useClasses,
      exclusive: PR.exclusive,
      eventid: PR.eventid,
      concurrentUses: PR.concurrentUses,
      maxConcurrent: PR.maxConcurrent,
      disabled: PR.disabled,
      venueId: PR.venueId,
      colour: PR.colour,
      orderindex: PR.orderindex,
      length: PR.length,
      classes: PR.classes,
      showonbooking: PR.showonbooking,
      resources: [innerPR],
      isresourcecontainer: PR.isresourcecontainer,
      isconstructed: PR.isconstructed,
      resourcegroupname: PR.resourcegroupname,
      price: PR.price,
      fixedprice: PR.fixedprice,
      tags: PR.tags,
      resourcegroup: PR.resourcegroup,
      selectionmode: PR.selectionmode,
      selectableresourcestrings: PR.selectableresourcestrings,
      startwhen: PR.startwhen,
      startTimeOffset: PR.startTimeOffset,
      optional: PR.optional,
      quantityselector: PR.quantityselector,
      quantity: PR.quantity,
      mode: PR.mode,
      inline: PR.inline,
      ignore: PR.ignore,
      admissions: PR.admissions,
      inProgress: PR.inProgress,
      originatingid: PR.originatingid,
      wasoptional: PR.wasoptional,
      wasselected: PR.wasselected,
      isupgrade: PR.isupgrade,
      showinitinerary: PR.showinitinerary,
      showondaysheet: PR.showondaysheet,
      cansplit: PR.cansplit
    };
  }

  //The resources availability is asked with: the base list plus the booking's upgrade's own
  //resources, which neither the package nor the booking's package copy carries.
  withUpgradeResources(baseResources: any[], currentUpgrade: any): any[] {
    const resources = [...(baseResources ?? [])];

    if (currentUpgrade == null) {
      return resources;
    }

    //saveUpgradess pushes them on before its own check, so they can already be here.
    if (resources.some(res => res.isupgrade == true)) {
      return resources;
    }

    (currentUpgrade.upgrade?.resources ?? []).forEach(res => {
      resources.push({
        ...res,
        isupgrade: true,
        packageupgradeid: currentUpgrade.packageupgradeid
      });
    });

    return resources;
  }

  //The package upgrade the booking has, found from the upgrade flags on its resources - at
  //whatever depth they sit. The object returned is the one from upgrades, or null.
  upgradeOnBooking(upgrades: any[], bookingResources: any[]): any {
    let current: any = null;

    if ((upgrades ?? []).length == 0) {
      return current;
    }

    this.resourcesWithUpgrade(bookingResources).forEach(res => {
      upgrades.forEach(upgrade => {
        if (upgrade.packageupgradeid == res.packageupgradeid) {
          current = upgrade;
        }
      });
    });

    return current;
  }

  //Every resource marked as an upgrade, at whatever depth it is held.
  resourcesWithUpgrade(list: any[]): any[] {
    const found: any[] = [];

    (list ?? []).forEach(res => {
      if (res.isupgrade == true) {
        found.push(res);
      }

      if (Array.isArray(res.resources) && res.resources.length > 0) {
        found.push(...this.resourcesWithUpgrade(res.resources));
      }
    });

    return found;
  }

  //The availability slot at the same clock time as the booking (read with local getters on both
  //sides, as Edit Booking always has), or null when there is no room then.
  slotAtTime(slots: any[], time: any): any {
    const clock = (value: any) => ('00' + new Date(value).getHours()).slice(-2) + ":" + ('00' + new Date(value).getMinutes()).slice(-2);
    const wanted = clock(time);
    let found: any = null;

    (slots ?? []).forEach(element => {
      if (clock(element.time) == wanted) {
        found = element;
      }
    });

    return found;
  }

  //The booking rebuilt from an availability slot, ready to save - what bookSlot does before it
  //saves. Changes booking (and selectedPackage's resources, as bookSlot always has).
  prepareRebook(booking: any, selectedPackage: any, admissions: any[], slot: any, venueId: string): void {
    selectedPackage.resources.forEach(adm => {
      adm.admissions = admissions;
    });

    const totalNoOfPeople: number = 0;

    booking.packageId = selectedPackage.packageId;
    booking.guests = totalNoOfPeople;
    booking.startTime = slot.time;
    booking.PackageName = selectedPackage.name;
    booking.venueId = venueId;
    booking.resources = slot.resources;
    booking.Disabled = false;
    booking.outstanding = 0;
    booking.name = "";
    booking.salestype = 0;
  }

  //#endregion

  //#region Whole changes, for callers without Edit Booking's screen

  //Reads a booking and builds what Edit Booking builds when it opens one (loaddata): the package
  //in full and its row from the package list, the booking's own resources kept aside, the
  //package's admissions carrying the booking's numbers, the booked rooms marked, the upgrade.
  async loadForEdit(venueId: string, bookingId: string): Promise<bookingforedit> {
    const bookingResponse: any = await this.http.get(bmxurl + 'booking/' + venueId + '/' + bookingId).toPromise();

    if (!bookingResponse?.succeeded || bookingResponse.data == null) {
      throw new Error('That booking could not be opened.');
    }

    const booking = bookingResponse.data;

    const [pkgResponse, listResponse]: any[] = await Promise.all([
      this.http.get(bmxurl + 'package/' + venueId + '/' + booking.packageId).toPromise(),
      this.http.get(bmxurl + 'packagelist/' + venueId + '/true').toPromise()
    ]);

    const pkgData = pkgResponse?.data;

    if (pkgData == null) {
      throw new Error("The booking's package could not be read.");
    }

    const packageObj: any = {
      ...pkgData,
      startDate: new Date(pkgData.startDate),
      endDate: new Date(pkgData.endDate),
      discountstart: new Date(pkgData.discountstart),
      discountend: new Date(pkgData.discountend),
    };

    if (packageObj.maxcalendarmonths == 0 || packageObj.maxcalendarmonths == null)
      packageObj.maxcalendarmonths = 3;

    if (packageObj.bookingtimeout == 0)
      packageObj.bookingtimeout = 300;

    (packageObj.admissions ?? []).forEach(adm => {
      adm.uselinkedadmissions = adm.linkedadmissions != null;
      adm.uselinkeddiscounts = adm.linkeddiscounts != null;
    });

    if (booking.resources != null && booking.resources.length > 0)
      booking.previousadmissions = JSON.parse(JSON.stringify(booking.resources[0].admissions));

    booking.Package = packageObj;
    const bookedAdmissions: any[] = booking.resources?.[0]?.admissions ?? [];
    const bookingResources = [...(booking.resources ?? [])];
    booking.resources = packageObj.resources;

    const admissions = [...packageObj.admissions];
    admissions.forEach(SA => {
      const original = bookedAdmissions.find(OSA => SA.admissionid == OSA.admissionid);
      SA.noofpeople = original != null ? original.noofpeople : 0;
    });

    booking.resources[0].admissions = admissions;

    const packages: any[] = (Array.isArray(listResponse) ? listResponse : listResponse?.data) ?? [];
    const selectedPackage = packages.find(element => element.packageId == booking.packageId);

    if (selectedPackage == null) {
      throw new Error("The booking's package could not be found in the package list.");
    }

    selectedPackage.disabled = false;

    fillselectableresourcestrings(selectedPackage.resources, packageObj.resources);

    const currentUpgrade = this.upgradeOnBooking(packageObj.packageupgrades ?? selectedPackage.packageupgrades,
      bookingResources.length > 0 ? bookingResources : booking.resources);

    this.markBookedResources(selectedPackage.resources, bookingResources);
    this.applyLinkedAdmissions(admissions);

    return { booking, bookingResources, selectedPackage, admissions, currentUpgrade };
  }

  //Sets new numbers on a loaded booking's admissions, checked against each admission's own
  //rules. counts maps admissionid to the new number. Linked admissions follow on their own.
  setAdmissionCounts(loaded: bookingforedit, counts: { [admissionid: string]: number }): void {
    Object.keys(counts).forEach(admissionid => {
      const adm = loaded.admissions.find(item => item.admissionid == admissionid);
      const count = Math.round(Number(counts[admissionid]));

      if (adm == null) {
        throw new Error('That admission is not on this package.');
      }

      if (adm.fixed == true) {
        throw new Error(adm.name + ' is worked out from other admissions, so it cannot be set directly.');
      }

      if (isNaN(count) || count < (Number(adm.min) || 0) || (adm.max != null && Number(adm.max) > 0 && count > Number(adm.max))) {
        throw new Error(adm.name + ' must be between ' + (Number(adm.min) || 0) + ' and ' + adm.max + '.');
      }

      adm.noofpeople = count;
    });

    this.applyLinkedAdmissions(loaded.admissions);

    if (!loaded.admissions.some(adm => (Number(adm.noofpeople) || 0) > 0)) {
      throw new Error('A booking must keep at least one person.');
    }
  }

  //Saves the admissions set on a loaded booking, the way Edit Booking's Check then Save does:
  //availability at the booking's own time for the new numbers, then the rebuild and save.
  //Returns the saved booking. Throws, changing nothing, when there is no room.
  async saveAdmissions(venueId: string, loaded: bookingforedit, allowResourcesChangedManually: boolean): Promise<any> {
    const booking = loaded.booking;
    const chosen = this.chosenResources(loaded.selectedPackage.resources, allowResourcesChangedManually);

    const req = this.moveBookingService.availabilityRequest(venueId, loaded.selectedPackage.packageId, loaded.admissions,
      this.withUpgradeResources(chosen, loaded.currentUpgrade), booking.bookingId, new Date(booking.startTime));

    const response: any = await this.http.post(bmxurl + '/Availability', withSelectableIds(req)).toPromise();
    const slot = this.slotAtTime(response?.data ?? [], booking.startTime);

    if (slot == null) {
      throw new Error('There is not room for that many people at the booking\'s time, so nothing was changed.');
    }

    this.prepareRebook(booking, loaded.selectedPackage, loaded.admissions, slot, venueId);

    if (!this.moveBookingService.hasAdmissions(booking)) {
      throw new Error('The change was stopped because the booking would have lost its admissions. Nothing was changed.');
    }

    const saved: any = await this.http.put(bmxurl + '/savebooking/true', withSelectableIds(booking)).toPromise();

    if (!saved?.succeeded) {
      throw new Error(saved?.messages?.[0] ?? 'The booking could not be saved.');
    }

    return saved.data?.bookingObj ?? saved.data;
  }

  //#endregion
}
